const HEADER_GAP = 12;
const FLOATING_BUTTON_SIZE = 64;
const FALLBACK_HEADER_HEIGHT = 64;

type FloatingElement = HTMLElement & {
  floatingButtonDragState?: { buttonHeight?: number };
  floatingButtonOffset?: number;
  handleFloatingButtonPointerMove?: (event: PointerEvent) => void;
};

let resizeListener: (() => void) | null = null;
let restoreDragPatch: (() => void) | null = null;

const getHeaderOffset = () => {
  const header =
    document.querySelector(".ApplicationHeader") ??
    document.querySelector("header") ??
    document.querySelector('[role="banner"]');
  return header?.getBoundingClientRect().height ?? FALLBACK_HEADER_HEIGHT;
};

const updateFloatingOffset = (host: HTMLElement) => {
  const offset = Math.max(20, Math.ceil(getHeaderOffset() + HEADER_GAP));
  host.dataset.floatingOffset = String(offset);
};

const applyDragPatch = () => {
  const prototype = window.customElements.get("rio-assist-widget")?.prototype as FloatingElement | undefined;
  const originalMove = prototype?.handleFloatingButtonPointerMove;
  if (!prototype || !originalMove || restoreDragPatch) {
    return;
  }

  prototype.handleFloatingButtonPointerMove = function patchedMove(this: FloatingElement, event: PointerEvent) {
    const buttonHeight = this.floatingButtonDragState?.buttonHeight ?? FLOATING_BUTTON_SIZE;
    const minPointerY = getHeaderOffset() + HEADER_GAP + buttonHeight / 2;
    const pointerEvent =
      event.clientY >= minPointerY
        ? event
        : new PointerEvent(event.type, {
            bubbles: true,
            cancelable: true,
            clientX: event.clientX,
            clientY: minPointerY,
            pointerId: event.pointerId,
            pointerType: event.pointerType,
          });

    originalMove.call(this, pointerEvent);

    if (typeof this.floatingButtonOffset === "number") {
      const maxOffset = Math.max(12, window.innerHeight - buttonHeight - getHeaderOffset() - HEADER_GAP);
      if (this.floatingButtonOffset > maxOffset) {
        this.floatingButtonOffset = maxOffset;
      }
    }
  };

  restoreDragPatch = () => {
    prototype.handleFloatingButtonPointerMove = originalMove;
    restoreDragPatch = null;
  };
};

export const applyFloatingPosition = (host: HTMLElement) => {
  cleanupFloatingPosition();
  updateFloatingOffset(host);
  resizeListener = () => updateFloatingOffset(host);
  window.addEventListener("resize", resizeListener);
  applyDragPatch();
};

export const cleanupFloatingPosition = () => {
  if (resizeListener) {
    window.removeEventListener("resize", resizeListener);
    resizeListener = null;
  }
  restoreDragPatch?.();
};
